import {
  CONSENT_TYPES,
  type ConsentAction,
  type ConsentRecord,
  type ConsentSource,
  type ConsentType,
  type Market,
  type User,
} from '../dataProvider/types';

/**
 * The consent-records query/aggregation module (D-consent-records, Phase 7 surface 2) - the ONE
 * place a member's consent is shaped for the surface. Consent is an APPEND-ONLY history; the
 * STANDING consent per type is DERIVED here as the LATEST record (by occurredAt, then id) for
 * that type, NEVER a stored flag (the deriveBalanceSigned pattern). The history is the single
 * source of truth, so the current state can never drift from it.
 *
 * Same assemble-from-data seam as buildDsarExportPackage / buildMemberEyeView: v1 aggregates
 * client-side from the mock provider; the real-data migration swaps this module for a
 * server-side endpoint without the surface changing.
 */

/** A type the member has never had a record for: standing state unknown, not a false "granted". */
export type ConsentStatus = 'granted' | 'not_granted' | 'unknown';

export interface ConsentHistoryRow {
  id: number;
  consentType: ConsentType;
  action: ConsentAction;
  source: ConsentSource;
  policyVersion: string;
  note: string | null;
  occurredAt: string;
}

export interface ConsentCurrentRow {
  consentType: ConsentType;
  status: ConsentStatus;
  /** occurredAt of the standing (latest) record, or null when none exists. */
  since: string | null;
  /** Source of the standing record, or null when none exists. */
  source: ConsentSource | null;
  /** Policy version of the standing record, or null when none exists. */
  policyVersion: string | null;
}

export interface ConsentView {
  subjectUserId: number;
  memberName: string;
  memberEmail: string;
  market: Market;
  /** One row per consent type, in the vocabulary order, with the derived standing state. */
  current: ConsentCurrentRow[];
  /** The full trail, newest first. */
  history: ConsentHistoryRow[];
}

export interface ConsentInput {
  user: User;
  records: ConsentRecord[];
}

/** The latest record wins: most recent occurredAt, ties broken by the higher id (later append). */
const isLater = (a: ConsentRecord, b: ConsentRecord): boolean =>
  a.occurredAt > b.occurredAt || (a.occurredAt === b.occurredAt && a.id > b.id);

/**
 * The standing record per type: the single latest record for that type, or undefined when the
 * type has never been seen. Exported so a test can assert the derivation directly, but the
 * surface reads it through buildConsentView.
 */
export const deriveStandingRecords = (
  records: ConsentRecord[],
): Map<ConsentType, ConsentRecord> => {
  const standing = new Map<ConsentType, ConsentRecord>();
  for (const r of records) {
    const current = standing.get(r.consentType);
    if (!current || isLater(r, current)) standing.set(r.consentType, r);
  }
  return standing;
};

const newestFirst = (a: ConsentHistoryRow, b: ConsentHistoryRow): number =>
  b.occurredAt.localeCompare(a.occurredAt) || b.id - a.id;

export function buildConsentView(input: ConsentInput): ConsentView {
  const { user } = input;
  const records = input.records.filter((r) => r.userId === user.id);
  const standing = deriveStandingRecords(records);

  const current: ConsentCurrentRow[] = CONSENT_TYPES.map((consentType) => {
    const record = standing.get(consentType);
    if (!record) {
      return {
        consentType,
        status: 'unknown' as ConsentStatus,
        since: null,
        source: null,
        policyVersion: null,
      };
    }
    return {
      consentType,
      status: record.action === 'grant' ? 'granted' : 'not_granted',
      since: record.occurredAt,
      source: record.source,
      policyVersion: record.policyVersion,
    };
  });

  const history: ConsentHistoryRow[] = records
    .map((r) => ({
      id: r.id,
      consentType: r.consentType,
      action: r.action,
      source: r.source,
      policyVersion: r.policyVersion,
      note: r.note,
      occurredAt: r.occurredAt,
    }))
    .sort(newestFirst);

  return {
    subjectUserId: user.id,
    memberName: user.name,
    memberEmail: user.email,
    market: user.market,
    current,
    history,
  };
}
